import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Play } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import ConfigureSimulationDialog from '@/routes/(app)/(scenarios)/(create)/components/configure-simulation-dialog';
import CreateScenarioStepper from '@/routes/(app)/(scenarios)/(create)/components/create-scenario-stepper';
import SelectedSourcesSummary from '@/routes/(app)/(scenarios)/(create)/components/selected-sources-summary';
import ShipmentPlanningEditor from '@/routes/(app)/(scenarios)/(create)/components/shipment-planning-editor';
import ShipmentPlanningStep from '@/routes/(app)/(scenarios)/(create)/components/shipment-planning-step';
import YardStep from '@/routes/(app)/(scenarios)/(create)/components/yard-step';
import { useCreateScenarioForm } from '@/routes/(app)/(scenarios)/(create)/hooks/use-create-scenario';

const CreateScenario = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Step 1 picks the yard and planning; step 2 gives the berth plan the page.
  const [isEditingPlanning, setIsEditingPlanning] = useState(false);
  const {
    form,
    configureDialog,
    isPlanningStepEnabled,
    areSourcesSelected,
    isLoadingYardProperties,
    hasYardPropertiesError,
    isCreatingScenario,
    handleSubmit,
    planningCutoffMs,
  } = useCreateScenarioForm();

  const configureAndNavigate = useCallback(async () => {
    const scenarioId = await handleSubmit();
    if (!scenarioId) return;
    await navigate({
      to: '/app/scenarios/$scenarioId',
      params: { scenarioId },
    });
  }, [handleSubmit, navigate]);

  const { yardId, planningId } = form.watch();
  const isOnPlanningStep = isEditingPlanning && areSourcesSelected;
  const planningStep = isOnPlanningStep ? 1 : 0;
  const activeStep = configureDialog.open ? 2 : planningStep;

  return (
    <Form {...form}>
      <div className='flex flex-wrap items-center justify-between gap-4'>
        <h1 className='text-xl font-semibold'>{t('scenarios.create.title')}</h1>
        <CreateScenarioStepper activeStep={activeStep} />
      </div>
      {isOnPlanningStep ? (
        <>
          <div className='flex flex-wrap items-center gap-3'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                setIsEditingPlanning(false);
              }}>
              <ArrowLeft />
              {t('scenarios.create.back')}
            </Button>
            <SelectedSourcesSummary
              yardId={yardId}
              planningId={planningId}
            />
            <Button
              size='sm'
              className='ml-auto'
              onClick={() => {
                configureDialog.onOpenChange(true);
              }}>
              <Play />
              {t('scenarios.create.configureAndRun')}
            </Button>
          </div>
          <ShipmentPlanningEditor
            planningId={planningId}
            cutoffMs={planningCutoffMs}
          />
        </>
      ) : (
        <>
          <div className='grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-2'>
            <YardStep />
            <ShipmentPlanningStep disabled={!isPlanningStepEnabled} />
          </div>
          <div className='sticky bottom-0 mt-auto flex flex-wrap items-center justify-between gap-3 border-t bg-background py-3'>
            <p className='text-sm text-muted-foreground'>
              {t('scenarios.create.preselectedHint')}
            </p>
            <div className='flex gap-2'>
              <Button
                variant='outline'
                onClick={async () => {
                  await navigate({ to: '/app/scenarios' });
                }}>
                {t('scenarios.create.cancel')}
              </Button>
              <Button
                disabled={!areSourcesSelected}
                onClick={() => {
                  setIsEditingPlanning(true);
                }}>
                {t('scenarios.create.continue')}
                <ArrowRight />
              </Button>
            </div>
          </div>
        </>
      )}
      <ConfigureSimulationDialog
        open={configureDialog.open}
        onOpenChange={configureDialog.onOpenChange}
        onSubmit={configureAndNavigate}
        isLoading={isLoadingYardProperties}
        hasError={hasYardPropertiesError}
        isSubmitting={isCreatingScenario}
      />
    </Form>
  );
};

export default CreateScenario;
